// Per-item audience: WHO the AI may share a knowledge item / document with. One picker, one
// chip list per community (Settings → Audiences), used on every knowledge surface.
// Chips are grouped Team / People; "Anyone" is the open default and clears the rest.
import { Globe, Lock, Users, Briefcase } from "lucide-react";
import { useAudiences, chipsOf, describeChips, isOpen, OPEN, STAFF } from "@/lib/audiences";

// Legacy words still understood by old callers.
export const AUDIENCE_LABEL = { anyone: "Anyone", residents: "Residents", board: "Board" };
export const AUDIENCE_OPTS = [
  { value: "anyone", label: "Anyone", desc: "Anyone who texts or calls the number — including people not on the roster." },
  { value: "residents", label: "Residents", desc: "Everyone approved on the roster (and the team)." },
  { value: "board", label: "Board", desc: "Board members and managers only." },
];

function toggle(list, key) {
  if (key === OPEN) return [OPEN];
  const cur = (list || []).filter((k) => k !== OPEN);
  return cur.includes(key) ? (cur.filter((k) => k !== key).length ? cur.filter((k) => k !== key) : [OPEN]) : [...cur, key];
}

function Chip({ active, disabled, onClick, testid, title, children, tone = "default" }) {
  const on = tone === "open" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-redline-500 bg-redline-50 text-redline-600";
  return (
    <button type="button" data-testid={testid} disabled={disabled} onClick={onClick} title={title} aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${active ? on : "border-sand-200 text-ink/60 hover:border-ink bg-white"}`}>
      {children}
    </button>
  );
}

/**
 * <AudiencePicker tenantId audiences onAudiences requireVerification readOnly testid />
 *  - audiences: array of chip keys (["anyone"] = open)
 *  - requireVerification: when true, "Anyone" is refused (a texted code proves phone possession, not membership)
 */
export function AudiencePicker({ tenantId, audiences, onAudiences, requireVerification = false, readOnly = false, testid = "audience", compact = false, onRefuseOpen, excludeSources = [] }) {
  const aud = useAudiences(tenantId);
  const { loading, labels } = aud;
  const people = aud.people.filter((a) => !excludeSources.includes(a.auto_source || ""));
  const team = aud.team.filter((a) => !excludeSources.includes(a.auto_source || ""));
  const cur = audiences && audiences.length ? audiences : [OPEN];
  const open = isOpen(cur);
  const pick = (key) => {
    if (key === OPEN && requireVerification) { onRefuseOpen?.(); return; }
    onAudiences(toggle(cur, key));
  };
  if (loading) return <div className="text-xs text-muted-foreground" data-testid={`${testid}-loading`}>Loading audiences…</div>;
  return (
    <div data-testid={`${testid}-picker`} className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip testid={`${testid}-anyone`} active={open} disabled={readOnly} onClick={() => pick(OPEN)} tone="open"
          title="Anyone who texts or calls the number — including people not on the roster">
          <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" /> Anyone</span>
        </Chip>
        {!compact && <span className="text-[11px] text-ink/40 mx-1">or only</span>}
        {people.map((a) => (
          <Chip key={a.key} testid={`${testid}-${a.key}`} active={!open && cur.includes(a.key)} disabled={readOnly} onClick={() => pick(a.key)}
            title={a.description || a.label}>
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {a.label}</span>
          </Chip>
        ))}
        {team.map((a) => (
          <Chip key={a.key} testid={`${testid}-${a.key}`} active={!open && cur.includes(a.key)} disabled={readOnly} onClick={() => pick(a.key)}
            title={(a.description || a.label) + (a.key === STAFF ? " — managers hear everything regardless" : "")}>
            <span className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" /> {a.label}</span>
          </Chip>
        ))}
      </div>
      {!compact && (
        <p className="text-[11px] text-muted-foreground" data-testid={`${testid}-summary`}>
          {open ? "Shared with anyone who asks." : `Shared only with: ${describeChips(cur, labels)}. Everyone else is told it's reserved.`}
        </p>
      )}
    </div>
  );
}

// Read-only chip line for lists and cards.
export function AudienceChips({ tenantId, item, chips, className = "" }) {
  const { labels } = useAudiences(tenantId);
  const c = chips || chipsOf(item);
  const open = isOpen(c);
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`} data-testid="audience-chips">
      {open ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold"><Globe className="h-2.5 w-2.5" /> Anyone</span>
      ) : c.map((k) => (
        <span key={k} className="inline-flex items-center gap-1 rounded-full bg-redline-50 text-redline-600 border border-redline-200 px-2 py-0.5 text-[10px] font-bold"><Lock className="h-2.5 w-2.5" /> {labels[k] || k}</span>
      ))}
    </span>
  );
}

// Legacy badge API kept for existing call sites: <AudienceBadge d={doc} tenantId compact />
export function AudienceBadge({ d, tenantId, compact = false }) {
  const { labels } = useAudiences(tenantId);
  const c = chipsOf(d);
  const open = isOpen(c);
  const text = open ? "Anyone" : describeChips(c, labels);
  if (compact) {
    return open ? null : (
      <span data-testid="audience-badge" title={`Shared only with: ${text}`}
        className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold bg-redline-50 text-redline-600 border border-redline-200">
        <Lock className="h-2.5 w-2.5" /> {text}
      </span>
    );
  }
  return (
    <span data-testid="audience-badge" title={open ? "Anyone who asks" : `Shared only with: ${text}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${open ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-redline-50 text-redline-600 border border-redline-200"}`}>
      {open ? <Globe className="h-2.5 w-2.5" /> : <Lock className="h-2.5 w-2.5" />} {text}
    </span>
  );
}
